/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { ContactFiltersAtom } from "../../atoms/ContactFiltersAtom";
import { LabelAtom } from "../../atoms/LabelAtom";
import useClickOutside from "../../hook/useClickOutside";
import { standardLabels } from "../../styles/labels";
import { ILabel } from "../../types/LabelTypes";
import { useModal } from "../Modal/Modal";
import CreateLabel from "./CreateLabel";
import ManageLabel from "./ManageLabel";

const Container = styled.div`
  position: relative;
  margin-left: 5px;
  font-weight: normal;
`;

const EmptyLabel = styled.div`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: white;
  border: 2px solid black;
`;

const LabelList = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  height: 15px;
  cursor: pointer;
`;

const abovePopover = css`
  bottom: 30px;
  right: -15px;
`;

const normalPopover = css`
  top: 20px;
  left: 0;
  right: 0;

  @media screen and (max-width: 600px) {
    left: unset;
  }
`;

const LabelPopover = styled.div<{ above?: boolean }>`
  position: absolute;
  background: white;
  z-index: 9999;
  height: fit-content;
  width: 200px;
  overflow: auto;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;

  display: flex;
  flex-direction: column;
  padding: 10px;
  ${(props) => (props.above ? abovePopover : normalPopover)}
`;

const Title = styled.div`
  color: #888888;
  font-size: 14px;
  padding: 10px;
`;

const Icon = styled.img`
  width: 15px;
  height: 15px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
    border-radius: 5px;
  }
`;

const Label = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid;
`;

const BorderWhite = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background-color: white;
  position: absolute;
`;

const LabelName = styled.div`
  margin-left: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const Config = styled.div`
  display: flex;
  flex-direction: column;
`;

const Check = styled.img`
  width: 10px;
  height: 10px;
`;

const Divider = styled.div`
  border-top: 1px solid #eaeaea;
  margin: 10px -10px;
`;

interface Props {
  above?: boolean;
  customCss?: any;
  leftDirection?: boolean;
  activeLabels: ILabel[];
  selectLabelHandler: (label: ILabel) => void;
  readOnly?: boolean;
  noFilter?: boolean;
  labelsIdsToRemove?: string[];

  onDropViewOpened?: (open: boolean) => void;
}

export default function LabelSelect(props: Props): JSX.Element {
  const {
    above,
    customCss,
    leftDirection,
    selectLabelHandler,
    activeLabels,
    readOnly,
    onDropViewOpened,
    noFilter,
    labelsIdsToRemove,
  } = props;

  const modalHandler = useModal();
  const labels = useRecoilValue(LabelAtom);

  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const [markedLabels, setMarkedLabels] = useState<ILabel[]>([]);

  const [, setContactFilters] = useRecoilState(ContactFiltersAtom);

  const rowRefFirst = useRef<any>(null);
  const rowRefSecond = useRef<any>(null);

  const wrapperRef = useClickOutside(() => setOpenDropView(false));

  useEffect(() => {
    setMarkedLabels(activeLabels);
  }, [activeLabels]);

  useEffect(() => {
    if (props.onDropViewOpened) props.onDropViewOpened(openDropView);
  }, [openDropView]);

  function handleSelectLabel(label: ILabel) {
    const id = label._id;
    if (labels) {
      setMarkedLabels((prevMarkedLabels) => {
        if (prevMarkedLabels.find(({ _id: markedId }) => markedId === id)) {
          return prevMarkedLabels.filter(
            ({ _id: markedId }) => markedId !== id
          );
        }
        return [...prevMarkedLabels, label];
      });

      if (!noFilter) {
        setContactFilters((oldFilter) => ({
          ...oldFilter,
        }));
      }

      selectLabelHandler(label);
      setOpenDropView(false);
    }
  }

  return (
    <Container ref={wrapperRef} css={customCss}>
      <LabelList
        onClick={() => {
          if (!readOnly) setOpenDropView(!openDropView);
        }}
      >
        {markedLabels.length ? (
          markedLabels
            .slice()
            .sort((a, b) => a.labelIndex - b.labelIndex)
            .map((elem, index, array) => {
              const labelCss = standardLabels.find(
                ({ color }) => color === elem.color
              );
              if (index < 5)
                return (
                  <BorderWhite
                    key={`label_list_${elem._id}`}
                    css={css`
                      ${leftDirection
                        ? css`
                            right: ${index * 8}px;
                          `
                        : css`
                            left: ${index * 8}px;
                          `}

                      z-index: ${array.length - index};
                    `}
                  >
                    <Label
                      css={css`
                        background-color: ${labelCss?.color || elem.color};
                        border-color: ${labelCss?.borderColor || elem.color};
                      `}
                    />
                  </BorderWhite>
                );
            })
        ) : (
          <BorderWhite
            css={
              leftDirection
                ? css`
                    right: 1px;
                  `
                : css`
                    left: 1px;
                  `
            }
          >
            <EmptyLabel
              onClick={() => {
                if (!readOnly) setOpenDropView(!openDropView);
              }}
            />
          </BorderWhite>
        )}
      </LabelList>

      {openDropView && (
        <LabelPopover above={above} className="labelpopover">
          <Title>Label</Title>
          {labels &&
            labels.length > 0 &&
            labels
              .slice()
              .filter((l) => !labelsIdsToRemove?.includes(l._id))
              .sort((a, b) => a.labelIndex - b.labelIndex)
              .map((elem) => {
                const labelCss = standardLabels.find(
                  ({ color }) => color === elem.color
                );
                return (
                  <Row
                    key={`label_row_${elem._id}`}
                    onClick={() => handleSelectLabel(elem)}
                  >
                    <Label
                      css={css`
                        background-color: ${labelCss?.color || elem.color};
                        border-color: ${labelCss?.borderColor || elem.color};
                      `}
                    >
                      {markedLabels.find(
                        ({ name: markedName, color: markedColor }) =>
                          markedName === elem.name && markedColor === elem.color
                      ) && <Check src={"/icons/check.svg"} />}
                    </Label>
                    <LabelName>{elem.name}</LabelName>
                  </Row>
                );
              })}

          <Divider />

          <Config>
            <Row
              onClick={() => {
                modalHandler.pushModal(<CreateLabel />, "Create new label");
                setOpenDropView(false);
              }}
              onMouseOver={() => {
                rowRefFirst.current.src = "/icons/sidebar/more-white.svg";
              }}
              onMouseLeave={() => {
                rowRefFirst.current.src = "/icons/sidebar/more.svg";
              }}
            >
              <Icon
                ref={rowRefFirst}
                id="create-icon"
                src={"/icons/sidebar/more.svg"}
              />
              <LabelName>Create new label</LabelName>
            </Row>
            {labels && !!labels.length && (
              <Row
                onClick={() => {
                  if (!labels) return;
                  modalHandler.pushModal(
                    <ManageLabel
                      labels={labels}
                      removeLabel={(label: ILabel) => console.log(label)}
                    />,
                    "Manage Labels"
                  );
                  setOpenDropView(false);
                }}
                onMouseOver={() => {
                  rowRefSecond.current.src =
                    "/icons/sidebar/settings-white.svg";
                }}
                onMouseLeave={() => {
                  rowRefSecond.current.src = "/icons/sidebar/settings.svg";
                }}
              >
                <Icon
                  ref={rowRefSecond}
                  className="icon"
                  src={"/icons/sidebar/settings.svg"}
                />
                <LabelName>Manage Labels</LabelName>
              </Row>
            )}
          </Config>
        </LabelPopover>
      )}
    </Container>
  );
}
